import { useFormikContext } from 'formik'
import Styles from './Images.module.scss'
import { ImagesFieldProps } from 'components/Wizards/SelectImages/Types'
import { ImageCheckbox } from 'components/Wizards/SelectImages/ImageCheckbox'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { InnerCarousel } from 'components/Carousel/Carousel'
import { useImages, useUnsplashImages } from 'components/Wizards/SelectImages/ImagesProvider'
import { ImageSkeleton } from './ImageSkeleton'
import { ErrorImage } from 'components/Wizards/SelectImages/ErrorImage'
import { DeclarativeToast } from 'common/ToastMessage/DeclarativeToast'
import { ToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useIsMutating } from 'hooks/useIsMutating'
import { Image } from 'components/Wizards/SelectImages/Image'
import { Text } from 'blaze-ui/Text/Text'
import { AlertTriangle } from 'blaze-ui/icons'

const EmptyState = () => (
  <div className={Styles.emptyState}>
    <AlertTriangle />
    <Text variant="secondary" color="var(--dark-80)">
      We couldn't find any stock image for this topic. Click the button below to add images from other sources.
    </Text>
  </div>
)

const UnsplashLoadingList = () => {
  const { maxPhotos } = useUnsplashImages()

  return (
    <>
      {Array.from({ length: maxPhotos }).map((_, index) => (
        <Image key={index}>
          <ImageSkeleton />
        </Image>
      ))}
    </>
  )
}

const UnsplashPhotosList = () => {
  const { values } = useFormikContext()
  const { photos, isLoading, isError, retry } = useUnsplashImages()

  if (isError) {
    return <ErrorImage onTryAgain={() => retry(values)} />
  }

  if (isLoading) {
    return null
  }

  return (
    <>
      {photos.map((photo) => (
        <ImageCheckbox
          key={photo.id}
          url={photo.urls.small}
          value={photo.urls.full}
          alt={photo.alt_description || photo.description || ''}
        />
      ))}
    </>
  )
}

export const ImagesField = ({ type = 'blog_post', embla }: ImagesFieldProps) => {
  const { images } = useImages()
  const generatingImages = useIsMutating({ mutationKey: 'generateImage' })
  const unsplash = useUnsplashImages()
  const noResults = images.length === 0 && unsplash.noResults

  const imagesList = (images || []).map((image, index) => (
    <ImageCheckbox url={image} alt={image} key={`image-${index}`} />
  ))

  return (
    <>
      {!!generatingImages && (
        <DeclarativeToast>
          <ToastMessage toastMessage="Generating images..." variant="generating" />
        </DeclarativeToast>
      )}
      <fieldset className={Styles.fieldset}>
        <legend className={Styles.legend}>
          {type === 'blog_post'
            ? 'Pick up to 3 image suggestions to add to the post'
            : 'Pick one or more of the image suggestions to add to the post'}
        </legend>
        <ResponsiveDesign.Desktop>
          <div className={Styles.images}>
            {noResults && <EmptyState />}
            {!unsplash.noResults && <UnsplashPhotosList />}
            {imagesList}
            {unsplash.isLoading && <UnsplashLoadingList />}
          </div>
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          {noResults && <EmptyState />}
          <InnerCarousel embla={embla}>
            {!unsplash.noResults && <UnsplashPhotosList />}
            {imagesList}
            {unsplash.isLoading && <UnsplashLoadingList />}
          </InnerCarousel>
        </ResponsiveDesign.Mobile>
      </fieldset>
    </>
  )
}
